import React, { useState } from "react";

const Win98Popup = ({ title, content, onClose, position }) => {
  // Check if we're on mobile
  const isMobile = window.innerWidth < 768;

  // For mobile, use fixed positioning in the center of the screen
  const mobileStyles = {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "80vh", // Maximum height of 80% of viewport height
    width: "90vw", // 90% of viewport width
    maxWidth: "400px", // Maximum width
  };

  // Centered desktop positioning
  const desktopStyles = {
    left: Math.min(Math.max(position.x, 200), window.innerWidth - 200),
    top: position.y,
    transform: "translate(-50%, -50%)", // Center the popup
    maxWidth: "400px",
  };

  return (
    <>
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      <div
        className="fixed z-50"
        style={isMobile ? mobileStyles : desktopStyles}
      >
        <div className="bg-gray-200 border-2 border-gray-700 shadow-lg w-full">
          <div className="bg-blue-800 text-white px-2 py-1 flex justify-between items-center">
            <span className="truncate">{title}</span>
            <button
              className="w-6 h-6 bg-gray-200 border-2 border-gray-400 shadow flex items-center justify-center hover:bg-red-500 active:shadow-inner"
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <div className="bg-white border-l border-t border-gray-400 shadow-inner">
            <div
              className="p-4 overflow-auto"
              style={
                isMobile
                  ? { maxHeight: "calc(80vh - 80px)" }
                  : { maxHeight: "60vh" }
              }
            >
              <p className="text-base leading-relaxed whitespace-pre-wrap">
                {content}
              </p>
            </div>

            {isMobile && (
              <div className="p-2 border-t border-gray-400 bg-gray-100 flex justify-center">
                <button
                  className="px-8 py-1 bg-gray-200 border-2 border-gray-400 shadow hover:bg-gray-300 active:shadow-inner"
                  onClick={onClose}
                >
                  OK
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const ClickableArabicText = ({ text, translation, title }) => {
  const [popup, setPopup] = useState(null);

  const handleClick = (e) => {
    e.preventDefault();
    if (popup) {
      setPopup(null);
    } else {
      setPopup({
        title,
        content: translation,
        position: {
          x: e.clientX || window.innerWidth / 2, // Fallback for touch events
          y: e.clientY || window.innerHeight / 2,
        },
      });
    }
  };

  return (
    <div className="relative">
      <div
        className="text-right text-xl my-2 cursor-help hover:bg-blue-50 p-2 rounded leading-loose"
        onClick={handleClick}
        dir="rtl"
      >
        <p className="arabic-text">{text}</p>
        {/* Optional indicator for clickable text */}
        <div className="text-xs text-gray-500 text-left mt-1"></div>
      </div>
      {popup && (
        <Win98Popup
          title={popup.title}
          content={popup.content}
          position={popup.position}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
};

const TabButton = ({ active, children, onClick }) => (
  <button
    className={`px-4 py-2 border-2 border-gray-400 ${
      active
        ? "bg-gray-300 border-b-0 relative -mb-0.5"
        : "bg-gray-200 hover:bg-gray-300"
    }`}
    onClick={onClick}
  >
    {children}
  </button>
);

const App = () => {
  const [selectedPrinciple, setPrinciple] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState("introduction");

  // Your complete data object here...
  const data = {
    title: "القواعد الأربع",
    introduction: {
      opening_dua: {
        arabic:
          "أسأل الله الكريم ربّ العرش العظيم أن يتولاّك في الدنيا والآخرة، وأن يجعلك مبارَكـًا أينما كنت، وأن يجعلك ممّن إذا أُعطيَ شكر، وإذا ابتُلي صبر، وإذا أذنب استغفر، فإنّ هؤلاء الثلاث عنوان السعادة.",
        translation:
          "Aku memohon kepada Allah Yang Maha Mulia, Tuhan 'Arsy yang agung agar melindungimu di dunia dan akhirat, menjadikanmu diberkahi di manapun engkau berada, dan menjadikanmu termasuk orang yang bersyukur bila diberi nikmat, bersabar bila diuji, dan beristighfar bila berbuat dosa. Sesungguhnya ketiga hal tersebut adalah tanda kebahagiaan.",
      },
      main_intro: {
        arabic:
          "اعلم أرشدك الله لطاعته: أن الحنيفيّة ملّة إبراهيم: أن تعبد الله مخلصـًا له الدين كما قال تعالى ﴿وَمَا خَلَقْتُ الْجِنَّ وَالْإِنسَ إِلَّا لِيَعْبُدُونِِ﴾[الذاريات:56]. فإذا عرفت أنّ الله خلقك لعبادته فاعلم: أنّ العبادة لا تسمّى عبادة إلا مع التوحيد، كما أنّ الصلاة لا تسمّى صلاة إلى مع الطهارة، فإذا دخل الشرك في العبادة فسدتْ كالحدَث إذا دخل في الطهارة.",
        translation:
          "Ketahuilah -semoga Allah membimbingmu kepada ketaatan kepada-Nya- bahwa al-Hanifiyyah (agama yang lurus) adalah millah (agama) Ibrahim, yaitu engkau beribadah hanya kepada Allah dengan mengikhlaskan agama untuk-Nya sebagaimana Allah berfirman: 'Dan tidaklah Aku menciptakan jin dan manusia melainkan agar mereka beribadah kepada-Ku.' Jika engkau telah mengetahui bahwa Allah menciptakanmu untuk beribadah kepada-Nya, maka ketahuilah bahwa ibadah tidak disebut ibadah kecuali dengan tauhid, sebagaimana shalat tidak disebut shalat kecuali dengan bersuci. Jika syirik masuk ke dalam ibadah, maka ia rusak sebagaimana hadats jika masuk ke dalam bersuci.",
      },
      warning: {
        arabic:
          "فإذا عرفتَ أن الشرك إذا خالط العبادة أفسدها وأحبط العمل وصار صاحبه من الخالدين في النار عرفتَ أنّ أهمّ ما عليك: معرفة ذلك، لعلّ الله أن يخلّصك من هذه الشَّبَكة، وهي الشرك بالله الذي قال الله فيه: ﴿إِنَّ اللَّهَ لَا يَغْفِرُ أَنْ يُشْرَكَ بِهِ وَيَغْفِرُ مَا دُونَ ذَلِكَ لِمَنْ يَشَاءُ﴾[النساء:116]",
        translation:
          "Jika engkau telah mengetahui bahwa syirik jika bercampur dengan ibadah akan merusaknya dan menghapuskan amal serta menjadikan pelakunya kekal di neraka, maka engkau akan mengetahui bahwa hal terpenting bagimu adalah mengetahui hal tersebut, semoga Allah menyelamatkanmu dari jaring ini, yaitu syirik kepada Allah yang Allah berfirman tentangnya: 'Sesungguhnya Allah tidak akan mengampuni dosa syirik, dan Dia mengampuni dosa yang selain dari (syirik) itu, bagi siapa yang dikehendaki-Nya.'",
      },
    },
    principles: [
      {
        number: 1,
        title: "القاعدة الأولى",
        content: {
          arabic:
            "أن تعلم أنّ الكفّار الذين قاتلهم رسول الله r يُقِرُّون بأنّ الله تعالى هو الخالِق المدبِّر، وأنّ ذلك لم يُدْخِلْهم في الإسلام",
          translation:
            "Hendaklah engkau mengetahui bahwa orang-orang kafir yang diperangi Rasulullah ﷺ mengakui bahwa Allah Ta'ala adalah Pencipta dan Pengatur, dan pengakuan itu tidak memasukkan mereka ke dalam Islam",
        },
        dalil: {
          arabic:
            "والدليل: قوله تعالى﴿قُلْ مَنْ يَرْزُقُكُمْ مِنْ السَّمَاءِ وَالْأَرْضِ أَمَّنْ يَمْلِكُ السَّمْعَ وَالْأَبْصَارَ وَمَنْ يُخْرِجُ الْحَيَّ مِنْ الْمَيِّتِ وَيُخْرِجُ الْمَيِّتَ مِنْ الْحَيِّ وَمَنْ يُدَبِّرُ الْأَمْرَ فَسَيَقُولُونَ اللَّهُ فَقُلْ أَفَلَا تَتَّقُونَ﴾[يونس:31]",
          translation:
            "Dan dalilnya adalah firman Allah Ta'ala: 'Katakanlah: Siapakah yang memberi rezeki kepadamu dari langit dan bumi, atau siapakah yang kuasa (menciptakan) pendengaran dan penglihatan, dan siapakah yang mengeluarkan yang hidup dari yang mati dan mengeluarkan yang mati dari yang hidup dan siapakah yang mengatur segala urusan? Maka mereka akan menjawab: Allah. Maka katakanlah: Mengapa kamu tidak bertakwa (kepada-Nya)?'",
        },
      },
      {
        number: 2,
        title: "القاعدة الثانية",
        content: {
          arabic:
            "أنّهم يقولون: ما دعوناهم وتوجّهنا إليهم إلا لطلب القُرْبة والشفاعة",
          translation:
            "Bahwa mereka berkata: Kami tidak berdoa dan menghadap kepada mereka kecuali untuk mencari kedekatan dan syafaat",
        },
        dalil_qurbah: {
          arabic:
            "فدليل القُربة قوله تعالى ﴿وَالَّذِينَ اتَّخَذُوا مِنْ دُونِهِ أَوْلِيَاءَ مَا نَعْبُدُهُمْ إِلَّا لِيُقَرِّبُونَا إِلَى اللَّهِ زُلْفَى إِنَّ اللَّهَ يَحْكُمُ بَيْنَهُمْ فِي مَا هُمْ فِيهِ يَخْتَلِفُونَ إِنَّ اللَّهَ لَا يَهْدِي مَنْ هُوَ كَاذِبٌ كَفَّارٌ﴾[الزمر:3]",
          translation:
            "Dalil tentang mencari kedekatan adalah firman Allah Ta'ala: 'Dan orang-orang yang mengambil pelindung selain Allah (berkata): Kami tidak menyembah mereka melainkan supaya mereka mendekatkan kami kepada Allah dengan sedekat-dekatnya. Sesungguhnya Allah akan memutuskan di antara mereka tentang apa yang mereka berselisih padanya. Sesungguhnya Allah tidak menunjuki orang-orang yang pendusta dan sangat ingkar'",
        },
        dalil_syafaat: {
          arabic:
            "ودليل الشفاعة قوله تعالى:﴿وَيَعْبُدُونَ مِنْ دُونِ اللَّهِ مَا لَا يَضُرُّهُمْ وَلَا يَنْفَعُهُمْ وَيَقُولُونَ هَؤُلَاءِ شُفَعَاؤُنَا عِنْدَ اللَّهِ﴾[يونس:18]",
          translation:
            "Dan dalil tentang syafaat adalah firman Allah Ta'ala: 'Dan mereka menyembah selain daripada Allah apa yang tidak dapat mendatangkan kemudharatan kepada mereka dan tidak (pula) kemanfaatan, dan mereka berkata: Mereka itu adalah pemberi syafaat kepada kami di sisi Allah'",
        },
      },
      {
        number: 3,
        title: "القاعدة الثالثة",
        content: {
          arabic:
            "أنّ النبي ﷺ ظهر على أُناسٍ متفرّقين في عباداتهم منهم مَن يعبُد الملائكة، ومنهم من يعبد الأنبياء والصالحين، ومنهم من يعبد الأحجار والأشجار، ومنهم مَن يعبد الشمس والقمر، وقاتلهم رسول الله ﷺ ولم يفرِّق بينهم",
          translation:
            "Bahwa Nabi ﷺ muncul pada manusia yang berbeda-beda dalam ibadah mereka. Di antara mereka ada yang menyembah malaikat, ada yang menyembah para nabi dan orang-orang shalih, ada yang menyembah batu dan pohon, ada yang menyembah matahari dan bulan. Rasulullah ﷺ memerangi mereka dan tidak membeda-bedakan di antara mereka",
        },
        dalil: {
          arabic:
            "والدليل قوله تعالى:﴿وَقَاتِلُوهُمْ حَتَّى لَا تَكُونَ فِتْنَةٌ وَيَكُونَ الدِّينُ لِلَّهِ﴾[البقرة:193]",
          translation:
            "Dan dalilnya adalah firman Allah Ta'ala: 'Dan perangilah mereka itu, sehingga tidak ada fitnah lagi dan (sehingga) ketaatan itu hanya semata-mata untuk Allah'",
        },
      },
      {
        number: 4,
        title: "القاعدة الرابعة",
        content: {
          arabic:
            "أنّ مشركي زماننا أغلظ شركـًا من الأوّلين، لأنّ الأوّلين يُشركون في الرخاء ويُخلصون في الشدّة، ومشركوا زماننا شركهم دائم؛ في الرخاء والشدّة",
          translation:
            "Bahwa orang-orang musyrik pada zaman kita lebih berat syiriknya daripada orang-orang terdahulu, karena orang-orang terdahulu berbuat syirik dalam keadaan senang dan mengikhlaskan ibadah dalam keadaan sulit, sedangkan orang-orang musyrik pada zaman kita syiriknya terus-menerus, baik dalam keadaan senang maupun sulit",
        },
        dalil: {
          arabic:
            "والدليل قوله تعالى: ﴿فَإِذَا رَكِبُوا فِي الْفُلْكِ دَعَوْا اللَّهَ مُخْلِصِينَ لَهُ الدِّينَ فَلَمَّا نَجَّاهُمْ إِلَى الْبَرِّ إِذَا هُمْ يُشْرِكُونَ﴾[العنكبوت:65]",
          translation:
            "Dan dalilnya adalah firman Allah Ta'ala: 'Maka apabila mereka naik kapal mereka mendoa kepada Allah dengan memurnikan ketaatan kepada-Nya; maka tatkala Allah menyelamatkan mereka sampai ke darat, tiba-tiba mereka (kembali) mempersekutukan (Allah)'",
        },
      },
    ],
  };

  const renderContent = () => {
    if (activeTab === "introduction") {
      return (
        <div className="p-4">
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-2">Opening Du'a</h3>
            <ClickableArabicText
              text={data.introduction.opening_dua.arabic}
              translation={data.introduction.opening_dua.translation}
              title="Opening Du'a Translation"
            />
          </div>

          <div className="mb-6">
            <h3 className="font-bold text-lg mb-2">Introduction</h3>
            <ClickableArabicText
              text={data.introduction.main_intro.arabic}
              translation={data.introduction.main_intro.translation}
              title="Introduction Translation"
            />
          </div>

          <div className="mb-6">
            <h3 className="font-bold text-lg mb-2">Warning</h3>
            <ClickableArabicText
              text={data.introduction.warning.arabic}
              translation={data.introduction.warning.translation}
              title="Warning Translation"
            />
          </div>
        </div>
      );
    }

    if (activeTab === "principles") {
      return (
        <div className="p-4">
          {selectedPrinciple ? (
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <button
                  onClick={() => setPrinciple(null)}
                  className="mr-2 px-2 py-1 bg-gray-200 border-2 border-gray-400 shadow hover:bg-gray-300 active:shadow-inner"
                >
                  ←
                </button>
                {selectedPrinciple.title}
              </h2>

              <div className="mb-6">
                <h3 className="font-bold mb-2">Main Content:</h3>
                <ClickableArabicText
                  text={selectedPrinciple.content.arabic}
                  translation={selectedPrinciple.content.translation}
                  title="Principle Content"
                />
              </div>

              {selectedPrinciple.dalil && (
                <div className="mb-6">
                  <h3 className="font-bold mb-2">Dalil:</h3>
                  <ClickableArabicText
                    text={selectedPrinciple.dalil.arabic}
                    translation={selectedPrinciple.dalil.translation}
                    title="Dalil"
                  />
                </div>
              )}

              {selectedPrinciple.dalil_qurbah && (
                <div className="mb-6">
                  <h3 className="font-bold mb-2">Dalil Al-Qurbah:</h3>
                  <ClickableArabicText
                    text={selectedPrinciple.dalil_qurbah.arabic}
                    translation={selectedPrinciple.dalil_qurbah.translation}
                    title="Dalil Al-Qurbah"
                  />
                </div>
              )}

              {selectedPrinciple.dalil_syafaat && (
                <div className="mb-6">
                  <h3 className="font-bold mb-2">Dalil Ash-Shafa'ah:</h3>
                  <ClickableArabicText
                    text={selectedPrinciple.dalil_syafaat.arabic}
                    translation={selectedPrinciple.dalil_syafaat.translation}
                    title="Dalil Ash-Shafa'ah"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {data.principles.map((principle) => (
                <button
                  key={principle.number}
                  className="p-4 bg-white border-2 border-gray-400 shadow hover:bg-blue-50 text-left"
                  onClick={() => setPrinciple(principle)}
                >
                  <h3 className="font-bold mb-2">{principle.title}</h3>
                  <p className="text-sm text-gray-600">
                    klik untuk melihat detail
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-teal-700 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Taskbar */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-200 border-t-2 border-white shadow-inner p-1">
          <button
            className="px-4 py-1 bg-gray-300 border-2 border-gray-400 shadow hover:bg-gray-400 active:shadow-inner flex items-center"
            onClick={() => setIsMinimized(false)}
          >
            <span className="w-4 h-4 bg-orange-600 rounded-sm mr-2"></span>
            <a href="https://www.fauzanlubis.com">fauzanlubis.com</a>
          </button>
        </div>

        {/* Main Window */}
        {!isMinimized && (
          <div className="bg-gray-200 border-2 border-gray-700 shadow-lg">
            {/* Title Bar */}
            <div className="bg-blue-800 text-white px-2 py-1 flex justify-between items-center">
              <div className="flex items-center">
                <span className="w-4 h-4 bg-orange-600 rounded-sm mr-2"></span>
                <span>Empat Landasan Utama</span>
              </div>
              <div className="flex">
                <button
                  className="w-6 h-6 bg-black-200 border-2 border-black-400 shadow flex items-center justify-center ml-1 hover:bg-gray-300 active:shadow-inner"
                  onClick={() => setIsMinimized(true)}
                >
                  _
                </button>
                <button className="w-6 h-6 bg-black-200 border-2 border-black-400 shadow flex items-center justify-center ml-1 hover:bg-gray-300 active:shadow-inner">
                  □
                </button>
                <button className="w-6 h-6 bg-black-200 border-2 border-black-400 shadow flex items-center justify-center ml-1 hover:bg-red-500 active:shadow-inner">
                  ×
                </button>
              </div>
            </div>

            {/* Tab Bar */}
            <div className="border-b-2 border-gray-400 px-2 pt-2 bg-gray-200 flex gap-1">
              <TabButton
                active={activeTab === "introduction"}
                onClick={() => setActiveTab("introduction")}
              >
                Introduction
              </TabButton>
              <TabButton
                active={activeTab === "principles"}
                onClick={() => setActiveTab("principles")}
              >
                Principles
              </TabButton>
            </div>

            {/* Content Area */}
            <div
              className="bg-white border-l border-t border-gray-400 shadow-inner overflow-auto"
              style={{ maxHeight: "calc(80vh - 170px)" }}
            >
              {renderContent()}
            </div>

            {/* Status Bar */}
            <div className="border-t-2 border-gray-400 p-1 text-sm bg-gray-200">
              Klik teks arabnya untuk melihat terjemahan
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
